import * as React from "react";

import { Button } from "@javascript/components/ui/button";
import { Input } from "@javascript/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@javascript/components/ui/popover";
import { cn } from "@javascript/components/utils";

export type TimezoneOption = {
  value: string;
  label: string;
  search_terms: string[];
};

type TimezonePickerProps = {
  id: string;
  name: string;
  value: string;
  timezones: TimezoneOption[];
  useDeviceTimezone: boolean;
};

export function TimezonePicker({ id, name, value, timezones, useDeviceTimezone }: TimezonePickerProps) {
  const initialTimezone = selectedTimezone(value, timezones, useDeviceTimezone);
  const [selected, setSelected] = React.useState(initialTimezone);
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const searchId = `${id}_search`;
  const listboxId = `${id}_results`;
  const matches = matchingTimezones(timezones, query);
  const selectedOption = timezones.find((timezone) => timezone.value === selected) ?? { value: selected, label: selected, search_terms: [] };

  function choose(timezone: TimezoneOption) {
    setSelected(timezone.value);
    setQuery("");
    setActiveIndex(-1);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (activeIndex >= 0) choose(matches[activeIndex]);
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    if (matches.length === 0) return;

    event.preventDefault();
    const step = event.key === "ArrowDown" ? 1 : -1;
    setActiveIndex((currentIndex) => {
      if (currentIndex < 0) return step === 1 ? 0 : matches.length - 1;

      return (currentIndex + step + matches.length) % matches.length;
    });
  }

  return (
    <>
      <input type="hidden" name={name} value={selected} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button id={id} type="button" variant="outline" className="min-h-11 w-full justify-between font-normal">
            <span className="truncate">{selectedOption.label}</span>
            <span className="sr-only">Timezone</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(calc(100vw-2rem),20rem)] p-3" onOpenAutoFocus={(event) => event.preventDefault()}>
          <Input
            id={searchId}
            role="combobox"
            aria-label="Search timezones"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={open}
            aria-activedescendant={activeIndex >= 0 ? `${id}_option_${activeIndex}` : undefined}
            autoFocus
            autoComplete="off"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search city, country, or offset"
          />
          <ul id={listboxId} role="listbox" aria-label="Timezone results" className="mt-2 max-h-64 overflow-y-auto rounded-md border">
            {matches.map((timezone, index) => (
              <li
                key={timezone.value}
                id={`${id}_option_${index}`}
                role="option"
                aria-selected={timezone.value === selected}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm whitespace-nowrap",
                  index === activeIndex && "bg-accent text-accent-foreground",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(timezone)}
              >
                {timezone.label}
              </li>
            ))}
          </ul>
          {matches.length === 0 && <p role="status" className="mt-2 text-sm text-muted-foreground">No timezones found.</p>}
        </PopoverContent>
      </Popover>
    </>
  );
}

function matchingTimezones(timezones: TimezoneOption[], query: string): TimezoneOption[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return timezones;

  return timezones.filter((timezone) => timezone.search_terms.some((term) => term.toLocaleLowerCase().includes(normalizedQuery)));
}

function selectedTimezone(value: string, timezones: TimezoneOption[], useDeviceTimezone: boolean): string {
  if (!useDeviceTimezone) return value;

  const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezones.some((timezone) => timezone.value === deviceTimezone) ? deviceTimezone : "UTC";
}
